<script setup lang="ts">
import { FormKitLazyProvider } from '@formkit/vue'
import type { ShiftExpense } from '~/schemas/shiftExpenses'

const { t } = useI18n()
const dialogRef = inject('dialogRef') as any
const expenseId = useDialogDataProperty<number>(dialogRef, 'expenseId')?.value
const expenseStore = useExpenseStore()
const { expenseTypes, fundSources } = storeToRefs(expenseStore)

const formData = ref()
const formKey = ref(0)
const expenseTypeId = ref<number | null>(null)
const expenseTypeError = ref('')
const fundSourceId = ref<number | null>(null)
const fundSourceError = ref('')

watch(expenseTypeId, (v) => {
  if (v) expenseTypeError.value = ''
})

watch(fundSourceId, (v) => {
  if (v) fundSourceError.value = ''
})

async function shiftExpenseSubmit(formData: ShiftExpense): Promise<void> {
  if (!expenseId) return

  if (!expenseTypeId.value) {
    expenseTypeError.value = t('Expense type is required')
    return
  }
  expenseTypeError.value = ''

  if (!fundSourceId.value) {
    fundSourceError.value = t('Fund source is required')
    return
  }
  fundSourceError.value = ''

  const label = expenseTypes.value.find(tp => tp.value === expenseTypeId.value)?.label ?? ''
  const success = await expenseStore.updateExpense(expenseId, {
    ...formData,
    expense_id: expenseTypeId.value,
    fund_source_id: fundSourceId.value,
    expense_type: label,
  })
  if (success) {
    dialogRef.value?.close()
  }
}

async function fetchData() {
  if (!expenseId) {
    useErrorToast(t('Expense ID is required'))
    return
  }

  const expense = await expenseStore.getExpense(expenseId)
  if (expense) {
    formData.value = expense
    expenseTypeId.value = expense.expense_id ?? null
    fundSourceId.value = expense.fund_source_id ?? null
    formKey.value++
  }
}

onMounted(async () => {
  await Promise.all([
    fetchData(),
    expenseStore.fetchExpenseTypes(),
    expenseStore.fetchFundSources(),
  ])
})
</script>

<template>
  
<FormKitLazyProvider config-file="true">
<FormKit
    id="edit-shift-expense"
    :key="formKey"
    class="flex"
    type="form"
    :value="formData"
    :actions="false"
    @submit="shiftExpenseSubmit"
  >
    <div
      v-if="formData && Object.keys(formData).length > 0"
      class="flex gap-3 justify-center"
    >
      <div class="w-1/2">
        <FormKit
          type="text"
          :label="t('Shift ID')"
          number
          placeholder="0"
          :disabled="true"
          validation="required"
          name="shift_id"
        />
        <FormKit
          type="text"
          :label="t('Expense ID')"
          number
          placeholder="0"
          :disabled="true"
          validation="required"
          name="id"
        />
        <div class="mb-3">
          <label class="block text-sm font-medium text-gray-700 mb-1">{{ t('Expense type') }}</label>
          <FormExpenseTypeSelect
            v-model="expenseTypeId"
            :options="expenseTypes"
            :allow-create="false"
          />
          <small
            v-if="expenseTypeError"
            class="text-red-500"
          >{{ expenseTypeError }}</small>
        </div>
        <div class="mb-3">
          <label class="block text-sm font-medium text-gray-700 mb-1">{{ t('Fund source') }}</label>
          <FormFundSourceSelect
            v-model="fundSourceId"
            :options="fundSources"
            :allow-create="false"
          />
          <small
            v-if="fundSourceError"
            class="text-red-500"
          >{{ fundSourceError }}</small>
        </div>
        <FormKit
          type="text"
          :label="t('Amount')"
          placeholder="0.00"
          validation="required|number"
          validation-visibility="live"
          name="amount"
        />
        <FormKit
          type="textarea"
          cols="20"
          :label="t('Notes')"
          name="notes"
        />
        <FormKit
          type="submit"
          :label="t('Update expense')"
        />
      </div>
    </div>
  </FormKit>
</FormKitLazyProvider>

</template>
